import React, { useState, useEffect, useRef } from 'react';

/**
 * LizMultimodalLive: A infraestrutura de voz de próxima geração para o SaaS Gestão Creche.
 * Utiliza o Gemini 2.0 Flash Multimodal Live API via WebSockets para interação Audio-to-Audio.
 */
export default function LizMultimodalLive({ 
  assistantName = 'Assistente da Creche',
  personality = 'Acolhedora',
  avatarUrl = '/assistente_avatar.svg',
  onSpeechEnd
}: {
  assistantName?: string,
  personality?: string,
  avatarUrl?: string,
  onSpeechEnd?: (text: string) => void
}) {
  const [isActive, setIsActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [volume, setVolume] = useState(0);
  const socketRef = useRef<WebSocket | null>(null);

  // Simulação de pulso visual do áudio (Acoustic Nuance Detection)
  useEffect(() => {
    let interval: any;
    if (isActive && isListening) {
      interval = setInterval(() => {
        setVolume(Math.random() * 100);
      }, 100);
    } else {
      setVolume(0);
    }
    return () => clearInterval(interval);
  }, [isActive, isListening]);

  const toggleLiveVoice = () => {
    if (!isActive) {
      // Aqui entrará a conexão WebSocket com o Endpoint do Gemini Multimodal Live
      console.log(`Iniciando conexão Live Audio-to-Audio com ${assistantName}...`);
      setIsActive(true);
      setIsListening(true);
    } else {
      console.log('Finalizando conexão Live.');
      setIsActive(false);
      setIsListening(false);
      if (socketRef.current) socketRef.current.close();
    }
  };

  return (
    <div className="liz-live-container" style={{
      background: 'linear-gradient(135deg, #fff5f7 0%, #ffe4ea 100%)',
      padding: '2rem',
      borderRadius: '24px',
      border: '2px solid #ffde e8',
      textAlign: 'center',
      boxShadow: '0 10px 30px rgba(255, 127, 164, 0.1)',
      margin: '1rem 0'
    }}>
      <div className="liz-avatar-wrapper" style={{ position: 'relative', marginBottom: '1.5rem' }}>
        {/* Círculos pulsantes de áudio de baixa latência */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isActive ? `${120 + volume * 0.5}px` : '100px',
          height: isActive ? `${120 + volume * 0.5}px` : '100px',
          borderRadius: '50%',
          backgroundColor: '#ff7fa422',
          transition: 'width 0.1s, height 0.1s',
          zIndex: 0
        }} />
        
        <img
          src={avatarUrl}
          alt={assistantName}
          style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            position: 'relative',
            zIndex: 1,
            border: isActive ? '4px solid #ff7fa4' : '4px solid #eee',
            boxShadow: isActive ? '0 0 20px rgba(255, 127, 164, 0.4)' : 'none',
            transition: 'all 0.3s'
          }}
        />
      </div>

      <h3 style={{ color: '#d63384', marginBottom: '0.5rem' }}>
        {isActive ? `Conversando com ${assistantName}` : `Chamar a ${assistantName}`}
      </h3>
      
      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
        {isActive 
          ? `Ela está ouvindo e sentindo seu tom de voz... (${personality})` 
          : `Clique no botão abaixo para iniciar uma conversa sem delays.`}
      </p>

      <button 
        onClick={toggleLiveVoice}
        style={{
          backgroundColor: isActive ? '#dc3545' : '#ff7fa4',
          color: 'white',
          border: 'none',
          padding: '1rem 2rem',
          borderRadius: '50px',
          fontSize: '1.1rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          margin: '0 auto'
        }}
        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        {isActive ? (
          <>
            <span style={{ width: '10px', height: '10px', backgroundColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' }} />
            Encerrar Conversa
          </>
        ) : (
          <>
            🎤 Iniciar Live Voice (Gemini 2.0 Flash)
          </>
        )}
      </button>

      {isActive && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '4px', height: '20px', alignItems: 'center' }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{
              width: '4px',
              height: `${Math.max(4, volume * (i * 0.2))}px`,
              backgroundColor: '#ff7fa4',
              borderRadius: '2px',
              transition: 'height 0.1s'
            }} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
